import React, {useEffect, useState} from 'react';
import {BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import {onAuthStateChanged} from 'firebase/auth';
import {auth} from "./firebase";
import LoginPage from "./pages/LoginPage";
import Welcome from "./components/Welcome";
import ChatPage from "./pages/ChatPage";
import MainPage from "./pages/MainPage";
import {Box, CircularProgress, CssBaseline} from "@mui/material";
import apiService from "./services/api";

const App = () => {
    return (
        <CssBaseline>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#080915', // Light grey background
            }}>
                <Box sx={{
                    width: '100%',
                    maxWidth: 500,
                    height: '100vh',
                    backgroundColor: 'white', // Background of the "mobile" container
                    boxShadow: 5, // Material-UI shadow styling
                    overflow: 'hidden', // Ensures nothing leaks outside the container
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* BrowserRouter should wrap all components that need routing */}
                    <BrowserRouter>
                        <RoutesWrapper/>
                    </BrowserRouter>
                </Box>
            </Box>
        </CssBaseline>
    );
};

// Wrapping component to handle all the routes and user logic
const RoutesWrapper = () => {
    const [user, setUser] = useState(null);
    const [authUser, setAuthUser] = useState(null);
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();


    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (authUser) => {
            setAuthUser(authUser);
        });
        return () => unsubscribe(); // Cleanup subscription on unmount
    }, [auth, authUser]);

    useEffect(() => {
        if (authUser) {
            setLoading(true);
            apiService.getUser().then((_user) => {
                setUser(_user);
                setLoading(false);
                const {from} = location.state || {from: {pathname: '/home'}};
                navigate(from, {replace: true});
            }).catch((error) => {
                setLoading(false);
                navigate('/welcome', {replace: true});
            });
        }

    }, [authUser]);

    // If loading auth state, show spinner
    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                <CircularProgress/>
            </Box>
        );
    }

    return (
        <Routes>
            <Route path="/login" element={<LoginPage user={user} setUser={setUser}/>}/>
            <Route path="/welcome" element={
                <PrivateRoute user={authUser}>
                    <Welcome setUser={setUser} authUser={authUser}/>
                </PrivateRoute>
            }/>
            <Route path="/home" element={
                <PrivateRoute user={authUser}>
                    <MainPage user={user} setUser={setUser}/>
                </PrivateRoute>
            }/>
            <Route path="/chat/:id" element={
                <PrivateRoute user={authUser}>
                    <ChatPage user={user}/>
                </PrivateRoute>
            }/>
            <Route path="/" element={
                <PrivateRoute user={authUser}>
                    <MainPage user={user} setUser={setUser}/>
                </PrivateRoute>
            }/>
        </Routes>
    );
};

// Component to handle private routing
const PrivateRoute = ({user, children}) => {
    const location = useLocation();
    if (!user) {
        return <Navigate to="/login" replace state={{from: location}}/>;
    }
    return children;
};

export default App;
